import React, { useEffect, useState } from 'react';
import SimpleSelect from '../../ui/SimpleSelect';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import { useConfirm } from '../ui/Modal';
import { useToast } from '../ui/Toast';

// Okno „Demo" w panelu super-admina: przedłużenie demo i przekształcenie w konto klienta
// (Login/backend/modules/demoLifecycle.js). Przed przekształceniem pokazuje, ile danych
// przykładowych zostanie usuniętych (GET …/demo-conversion-preview liczy to w transakcji
// wycofanej na końcu), i pyta, czy je usunąć — domyślnie tak. Plan po przekształceniu trzeba
// wybrać (FAZA F, F.3): bez cichego domyślnego, serwer bez planu odpowiada 400.
// Style: klasy z pages/SuperAdmin.css (modal-overlay, org-form…), jak pozostałe okna panelu.

function csrfToken() {
  const parts = `; ${document.cookie}`.split('; csrf=');
  return parts.length === 2 ? parts.pop().split(';').shift() : '';
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || '');
  return data;
}

export default function DemoManageModal({ org, onClose, onChanged }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();

  const [days, setDays] = useState('7');
  const [plan, setPlan] = useState('');
  const [wipe, setWipe] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/super-admin/organizations/${org.id}/demo-conversion-preview`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((data) => { if (!cancelled) setPreview(data.removed || null); })
      .catch(() => { if (!cancelled) setPreviewFailed(true); });
    return () => { cancelled = true; };
  }, [org.id]);

  const daysNumber = Number(days);
  const daysValid = Number.isInteger(daysNumber) && daysNumber >= 1 && daysNumber <= 365;
  const isPending = !org.access_expires_at;

  const handleExtend = async () => {
    if (!daysValid) return;
    setBusy(true);
    try {
      await postJson(`${API_BASE}/api/super-admin/organizations/${org.id}/extend-demo`, { days: daysNumber });
      toast.success(t('demoManage.extended'));
      onChanged();
    } catch (e) {
      toast.error(e.message || t('demoManage.error'));
    } finally {
      setBusy(false);
    }
  };

  const handleConvert = async () => {
    if (!plan) return;
    const ok = await confirm({
      title: wipe ? t('demoManage.confirmConvertWipe', { name: org.name }) : t('demoManage.confirmConvert', { name: org.name }),
      tone: wipe ? 'danger' : undefined,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await postJson(`${API_BASE}/api/super-admin/organizations/${org.id}/convert-demo`, { wipeSampleData: wipe, subscriptionPlan: plan });
      toast.success(t('demoManage.converted'));
      onChanged();
    } catch (e) {
      toast.error(e.message || t('demoManage.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} data-cy="demo-manage-modal">
        <div className="modal-header">
          <h2>{t('demoManage.title', { name: org.name })}</h2>
          <button className="close-btn" onClick={onClose} aria-label={t('common.cancel')}>×</button>
        </div>

        <div className="org-form">
          <h3>{t('demoManage.extendTitle')}</h3>
          <div className="form-group">
            <label htmlFor="demo-extend-days">{t('superAdmin.demoDaysLabel')}</label>
            <input
              id="demo-extend-days"
              type="number"
              min="1"
              max="365"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              data-cy="demo-extend-days"
            />
            <small>{isPending ? t('demoManage.extendPending') : t('demoManage.extendHint')}</small>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={handleExtend} disabled={busy || !daysValid} data-cy="demo-extend-submit">
              {isPending
                ? t('demoManage.setDaysButton', { days: daysValid ? daysNumber : '…' })
                : t('demoManage.extendButton', { days: daysValid ? daysNumber : '…' })}
            </button>
          </div>

          <h3>{t('demoManage.convertTitle')}</h3>
          <p><small>{t('demoManage.convertHint')}</small></p>
          <div className="form-group">
            <label>{t('demoManage.planLabel')}</label>
            <SimpleSelect
              style={{ width: '100%' }}
              value={plan}
              onChange={setPlan}
              placeholder={t('demoManage.planPlaceholder')}
              aria-label={t('demoManage.planLabel')}
              options={[
                { value: 'basic', label: 'Basic' },
                { value: 'premium', label: 'Premium' },
              ]}
              data-cy="demo-convert-plan"
            />
            <small>{t('demoManage.planHint')}</small>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={wipe} onChange={(e) => setWipe(e.target.checked)} data-cy="demo-convert-wipe" />
              {t('demoManage.wipeLabel')}
            </label>
            {wipe ? (
              <small data-cy="demo-convert-preview">
                {t('demoManage.wipeHint')}{' '}
                {preview
                  ? t('demoManage.previewItems', preview)
                  : previewFailed ? t('demoManage.previewFailed') : t('demoManage.previewLoading')}
                {preview && preview.companyData ? ` ${t('demoManage.previewCompany')}` : ''}
              </small>
            ) : (
              <small>{t('demoManage.keepHint')}</small>
            )}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
            <button type="button" className="btn-primary" onClick={handleConvert} disabled={busy || !plan} title={plan ? undefined : t('demoManage.planRequired')} data-cy="demo-convert-submit">
              {t('demoManage.convertButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
